import React, { useEffect, useState } from 'react';
import { withStyles, WithStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Grid, Paper, Typography, Button, Box, TextField, InputAdornment, IconButton, Popover, CircularProgress } from '@material-ui/core';
import { ChromePicker } from 'react-color';
import { Location, LocationReducerAction } from '../..';
import ImageCropper, { CropData } from '../../../../../ImageCropper';
import FileInput from '../../../../../FileUpload';
import ColorLensIcon from '@material-ui/icons/ColorLens';
import { fetchGoogleFonts, GoogleFont, getGoogleFontUrl, getGoogleFontCss, loadGoogleFont } from '../../../../../../utils/googleFonts';
import Autocomplete from '@material-ui/lab/Autocomplete';

const styles = (theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    sectionTitle: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
      color: theme.palette.text.primary,
    },
    sectionDescription: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
    },
    paper: {
      padding: theme.spacing(3),
      backgroundColor: theme.palette.background.default,
      marginBottom: theme.spacing(3),
    },
    inputField: {
      width: '100%',
      marginBottom: theme.spacing(2),
      '& .MuiInputBase-root': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    colorPreview: {
      width: 24,
      height: 24,
      borderRadius: '50%',
      marginRight: theme.spacing(1),
    },
    stickyButtonContainer: {
      position: 'fixed',
      bottom: theme.spacing(8),
      right: theme.spacing(8),
      zIndex: 1000,
    },
    stickyButton: {
      paddingInline: theme.spacing(4),
    },
  });

interface BrandingProps extends WithStyles<typeof styles> {
  location: Location;
  locationId: string | undefined;
  dispatch: React.Dispatch<{ type: LocationReducerAction; value: any }>;
  handleSave?: () => void;
  isLocationCreate: boolean;
}

const Branding: React.FC<BrandingProps> = ({ classes, location, locationId, dispatch, handleSave, isLocationCreate }) => {
  const [initialState, setInitialState] = useState<Location | null>(null);
  const [stateChanged, setStateChanged] = useState(false);
  const theme = useTheme();
  const [allFonts, setAllFonts] = useState<GoogleFont[]>([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [fontSearch, setFontSearch] = useState('');
  const [fontsLimit, setFontsLimit] = useState(100);

  useEffect(() => {
    const loadAllFonts = async () => {
      setIsLoadingFonts(true);
      try {
        const result = await fetchGoogleFonts({ page: 1, pageSize: 1000 });
        const filteredFonts = (result.fonts || []).filter((font) => font.subsets.includes('latin'));
        setAllFonts(filteredFonts);
      } catch (error) {
        setAllFonts([]);
      } finally {
        setIsLoadingFonts(false);
      }
    };
    loadAllFonts();
  }, []);

  const handleFontSelect = (font: GoogleFont) => {
    const fontFamily = font.family;
    const variants = ['400', '500', '600', '700'];
    const fontUrl = getGoogleFontUrl(fontFamily, variants);
    const fontCss = getGoogleFontCss(fontFamily);

    loadGoogleFont(fontFamily, variants);

    dispatch({ type: LocationReducerAction.SET_GOOGLE_FONT_FAMILY, value: fontFamily });
    dispatch({ type: LocationReducerAction.SET_GOOGLE_FONT_URL, value: fontUrl });
    dispatch({ type: LocationReducerAction.SET_GOOGLE_FONT_CSS, value: fontCss });
  };

  useEffect(() => {
    setInitialState(location);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialState && JSON.stringify(initialState) !== JSON.stringify(location)) {
      setStateChanged(true);
    } else {
      setStateChanged(false);
    }
  }, [location, initialState]);

  const [colorPickerAnchor, setColorPickerAnchor] = useState<null | HTMLElement>(null);
  const [currentColorField, setCurrentColorField] = useState<LocationReducerAction | null>(null);

  const handleClickOpen = (event: React.MouseEvent<HTMLButtonElement>, colorField: LocationReducerAction) => {
    setColorPickerAnchor(event.currentTarget);
    setCurrentColorField(colorField);
  };

  const handleClose = () => {
    setColorPickerAnchor(null);
    setCurrentColorField(null);
  };

  const handleColorChange = (color: any) => {
    if (currentColorField) {
      dispatch({ type: currentColorField, value: color.hex });
    }
  };

  const getColorValue = (color: string | null | undefined, defaultColor: string) => {
    return color || defaultColor;
  };
  const primaryColor = getColorValue(location.ordering_system_primary_color, '#3b4d19');
  const secondaryColor = getColorValue(location.ordering_system_secondary_color, '#7bbf40');

  let filteredFonts = allFonts;
  if (fontSearch) {
    filteredFonts = allFonts.filter((font) => font.family.toLowerCase().includes(fontSearch.toLowerCase()));
  }
  const isLimited = filteredFonts.length > fontsLimit;
  const fontsToShow = filteredFonts.slice(0, fontsLimit);
  const handleListboxScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const listboxNode = event.currentTarget;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
      if (fontsLimit < filteredFonts.length) {
        setFontsLimit((limit) => limit + 100);
      }
    }
  };
  useEffect(() => {
    setFontsLimit(100);
  }, [fontSearch]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Branding
        </Typography>
      </Grid>

      {/* Logo Section */}
      <Grid item xs={12} sm={4}>
        <Box>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Logo
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            Set the logo image for your online ordering store
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <Grid container justify="space-between">
            <Grid item>
              <Typography variant="subtitle1">Logo Image</Typography>
            </Grid>
            {(location.logo_image_data || location.logo_image_added) && (
              <Grid item>
                <Button variant="contained" onClick={() => dispatch({ type: LocationReducerAction.CLEAR_LOGO_IMAGE, value: null })}>
                  Clear selection
                </Button>
              </Grid>
            )}
          </Grid>
          {location.logo_image_data || location.logo_image_added ? (
            <Grid item xs={12}>
              <ImageCropper
                onCropComplete={(croppedImage) =>
                  dispatch({
                    type: LocationReducerAction.SET_LOGO_IMAGE,
                    value: croppedImage,
                  })
                }
                onCropChange={(cropData: CropData) =>
                  dispatch({
                    type: LocationReducerAction.SET_LOGO_IMAGE_CROP_DATA,
                    value: cropData,
                  })
                }
                initialCrop={location.logo_image_crop_data}
                image={location.logo_image_data}
                imageUrl={`${process.env.REACT_APP_AWS_ORDERING_SYSTEM_IMAGES_URL}/${locationId}-logo.jpeg`}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <FileInput
                label="Click to upload"
                onImageSelected={(img) =>
                  dispatch({
                    type: LocationReducerAction.SET_LOGO_IMAGE_DATA,
                    value: img,
                  })
                }
              />
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* Cover Image Section */}
      <Grid item xs={12} sm={4}>
        <Box>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Cover Image
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            Set the cover image for your online ordering store.
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <Grid container justify="space-between">
            <Grid item>
              <Typography variant="subtitle1">Header Image</Typography>
            </Grid>
            {(location.header_image_data || location.header_image_added) && (
              <Grid item>
                <Button variant="contained" onClick={() => dispatch({ type: LocationReducerAction.CLEAR_HEADER_IMAGE, value: null })}>
                  Clear selection
                </Button>
              </Grid>
            )}
          </Grid>
          {location.header_image_data || location.header_image_added ? (
            <Grid item xs={12}>
              <ImageCropper
                onCropComplete={(croppedImage) =>
                  dispatch({
                    type: LocationReducerAction.SET_HEADER_IMAGE,
                    value: croppedImage,
                  })
                }
                onCropChange={(cropData: CropData) =>
                  dispatch({
                    type: LocationReducerAction.SET_HEADER_IMAGE_CROP_DATA,
                    value: cropData,
                  })
                }
                initialCrop={location.header_image_crop_data}
                image={location.header_image_data}
                imageUrl={`${process.env.REACT_APP_AWS_ORDERING_SYSTEM_IMAGES_URL}/${locationId}.jpeg`}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <FileInput
                label="Click to upload"
                onImageSelected={(img) =>
                  dispatch({
                    type: LocationReducerAction.SET_HEADER_IMAGE_DATA,
                    value: img,
                  })
                }
              />
            </Grid>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Box>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Background Image
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            Upload a full screen image which will be used as the background image for the Start Order screen on the Online Store and Kiosk pages
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <Grid container justify="space-between">
            <Grid item>
              <Typography variant="subtitle1">Background Image</Typography>
            </Grid>
            {(location.background_image_data || location.background_image_added) && (
              <Grid item>
                <Button variant="contained" onClick={() => dispatch({ type: LocationReducerAction.CLEAR_BACKGROUND_IMAGE, value: null })}>
                  Clear selection
                </Button>
              </Grid>
            )}
          </Grid>
          {location.background_image_data || location.background_image_added ? (
            <Grid item xs={12}>
              <ImageCropper
                onCropComplete={(croppedImage) =>
                  dispatch({
                    type: LocationReducerAction.SET_BACKGROUND_IMAGE,
                    value: croppedImage,
                  })
                }
                onCropChange={(cropData: CropData) =>
                  dispatch({
                    type: LocationReducerAction.SET_BACKGROUND_IMAGE_CROP_DATA,
                    value: cropData,
                  })
                }
                initialCrop={location.background_image_crop_data}
                image={location.background_image_data}
                imageUrl={
                  location.background_image_data || location.background_image_added
                    ? `${process.env.REACT_APP_AWS_ORDERING_SYSTEM_IMAGES_URL}/${locationId}-background.jpeg`
                    : `${process.env.REACT_APP_AWS_ORDERING_SYSTEM_IMAGES_URL}/default-background.jpeg`
                }
                aspect={16 / 9}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <FileInput
                label="Click to upload"
                onImageSelected={(img) =>
                  dispatch({
                    type: LocationReducerAction.SET_BACKGROUND_IMAGE_DATA,
                    value: img,
                  })
                }
              />
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* Colors Section */}
      <Grid item xs={12} sm={4}>
        <Box>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Colours
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            Set the primary and secondary colors for your location.
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Primary"
                variant="outlined"
                value={primaryColor}
                onChange={(e) => handleColorChange({ hex: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box className={classes.colorPreview} style={{ backgroundColor: primaryColor }} />
                      <IconButton onClick={(event) => handleClickOpen(event, LocationReducerAction.SET_ORDERING_SYSTEM_PRIMARY_COLOR)}>
                        <ColorLensIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className={classes.inputField}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Secondary"
                variant="outlined"
                value={secondaryColor}
                onChange={(e) => handleColorChange({ hex: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box className={classes.colorPreview} style={{ backgroundColor: secondaryColor }} />
                      <IconButton onClick={(event) => handleClickOpen(event, LocationReducerAction.SET_ORDERING_SYSTEM_SECONDARY_COLOR)}>
                        <ColorLensIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                className={classes.inputField}
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              Button Preview
            </Typography>
            <Button
              variant="outlined"
              style={{
                backgroundColor: primaryColor,
                color: secondaryColor,
                marginTop: '8px',
              }}>
              Preview Button
            </Button>
            <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
              This shows how your buttons will appear with primary as background and secondary as text color
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Google Fonts Section */}
      <Grid item xs={12} sm={4}>
        <Box>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Typography
          </Typography>
          <Typography variant="body2" className={classes.sectionDescription}>
            Choose a Google Font for your location's text
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <Autocomplete
            onOpen={() => {
              if (fontsLimit > 200) setFontsLimit(100);
            }}
            options={fontsToShow}
            getOptionLabel={(option) => option.family}
            value={allFonts.find((f) => f.family === location.google_font_family) || undefined}
            onChange={(_, value) => value && handleFontSelect(value)}
            loading={isLoadingFonts}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') setFontSearch(value);
            }}
            inputValue={fontSearch}
            disablePortal
            ListboxProps={{
              style: {
                maxHeight: 400,
                minHeight: 200,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                overflow: 'auto',
              },
              onScroll: (e: React.UIEvent<HTMLDivElement>) => {
                handleListboxScroll(e);
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Font"
                variant="outlined"
                fullWidth
                helperText={isLimited ? `Showing first ${fontsLimit} results. Scroll to load more or refine your search` : 'Select a Google Font for your location'}
                FormHelperTextProps={{ style: { marginLeft: 0, marginTop: 12 } }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingFonts ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(option) => (
              <Box display="flex" alignItems="center">
                <span style={{ fontFamily: `'${option.family}', sans-serif`, fontSize: 16, color: theme.palette.text.primary }}>{option.family}</span>
              </Box>
            )}
            noOptionsText={isLoadingFonts ? <CircularProgress size={24} /> : 'No fonts found'}
            disableClearable
            autoHighlight
            fullWidth
            disableListWrap
          />
          {location.google_font_family && (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                Font Preview
              </Typography>
              <Typography variant="body2" className={classes.sectionDescription} style={{ marginBottom: 8 }}>
                This shows how your selected font will appear in your location's interface
              </Typography>
              <Box mt={6}>
                <Typography
                  variant="h5"
                  style={{
                    fontFamily: `'${location.google_font_family}', sans-serif`,
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    marginBottom: 8,
                  }}>
                  {location.google_font_family}
                </Typography>
                <Typography
                  variant="h6"
                  style={{
                    fontFamily: `'${location.google_font_family}', sans-serif`,
                    color: theme.palette.text.primary,
                    marginBottom: 8,
                  }}>
                  The quick brown fox jumps over the lazy dog
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    fontFamily: `'${location.google_font_family}', sans-serif`,
                    color: theme.palette.text.secondary,
                    marginBottom: 8,
                  }}>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in dui mauris. Vivamus hendrerit arcu sed erat molestie vehicula. Sed auctor neque eu tellus
                  rhoncus ut eleifend nibh porttitor
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>

      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <ChromePicker color={currentColorField === LocationReducerAction.SET_ORDERING_SYSTEM_PRIMARY_COLOR ? primaryColor : secondaryColor} onChange={handleColorChange} />
      </Popover>
      {stateChanged && !isLocationCreate && (
        <Box className={classes.stickyButtonContainer}>
          <Button variant="contained" color="primary" className={classes.stickyButton} onClick={handleSave}>
            Save changes
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export default withStyles(styles)(Branding);
